import { urlValidator } from "components/Form/Form.utils";
import { IconCollection } from "components/Icon/IconCollection";
import { ResourceConfig } from "config/config";
import { normalizeNullableStringFields } from "config/resourceUtils";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import { makeTestConnectionActionSignature } from "containers/Resources/components/CommonActions/testConnectionAction";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
    referencesUsedByTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import {
    buildSimpleColumn,
    isEnabledColumn,
} from "containers/Search/components/SearchTabs/columns";
import identity from "./identity";
import resource from "./resource";
import { isEnabledField } from "./sharedFields";

const resourceName = "ldap_config";

const config: ResourceConfig = {
    label: "LDAP config",
    icon: IconCollection.folderAccountOutline,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, commentsTab.tab, referencesUsedByTab.tab, metadataTab.tab],
                content: [
                    logsTab.content,
                    commentsTab.content,
                    referencesUsedByTab.content,
                    metadataTab.content,
                ],
            },
        },
    ],
    actions: {
        shown: [makeTestConnectionActionSignature({ resourceName })],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    additionalColumns: (setNewValue) => [
        isEnabledColumn(setNewValue),
        {
            label: "Server URL",
            key: "server_url",
            shown: true,
            sortable: true,
            dto: (value: any) => {
                const content = <div>{value.server_url}</div>;
                return buildSimpleColumn(
                    content,
                    setNewValue
                        ? () => setNewValue({ field: "server_url", value: value.server_url })
                        : undefined
                );
            },
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            ${identity.graphql.resourceBody}
            is_enabled
            server_url
            use_starttls
            cacert
            verify_tls
            client_cert
            username
            has_password
            password_marker
            authentication_mechanism
            base_dn
            users_filter
            username_attribute
            mail_attribute
            has_client_key
            client_key_marker
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            is_enabled
            server_url
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $is_enabled: Boolean,
            $server_url: String,
            $use_starttls: Boolean,
            $cacert: String,
            $verify_tls: Boolean,
            $client_cert: String,
            $client_key: String,
            $username: String,
            $password: String,
            $authentication_mechanism: String,
            $base_dn: String,
            $users_filter: String,
            $username_attribute: String,
            $mail_attribute: String,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            is_enabled: $is_enabled,
            server_url: $server_url,
            use_starttls: $use_starttls,
            cacert: $cacert,
            verify_tls: $verify_tls,
            client_cert: $client_cert,
            client_key: $client_key,
            username: $username,
            password: $password,
            authentication_mechanism: $authentication_mechanism,
            base_dn: $base_dn,
            users_filter: $users_filter,
            username_attribute: $username_attribute,
            mail_attribute: $mail_attribute,
        `,
        updateVariables: ` 
            ${resource.graphql.updateVariables(resourceName)}
            $is_enabled: Boolean,
            $server_url: String,
            $use_starttls: Boolean,
            $cacert: String,
            $verify_tls: Boolean,
            $client_cert: String,
            $client_key: String,
            $username: String,
            $password: String,
            $authentication_mechanism: String,
            $base_dn: String,
            $users_filter: String,
            $username_attribute: String,
            $mail_attribute: String,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            is_enabled: $is_enabled,
            server_url: $server_url,
            use_starttls: $use_starttls,
            cacert: $cacert,
            verify_tls: $verify_tls,
            client_cert: $client_cert,
            client_key: $client_key,
            username: $username,
            password: $password,
            authentication_mechanism: $authentication_mechanism,
            base_dn: $base_dn,
            users_filter: $users_filter,
            username_attribute: $username_attribute,
            mail_attribute: $mail_attribute,
        `,
    },
    form: [
        isEnabledField,
        {
            name: "server_url",
            component: "url",
            fieldProps: {
                label: "Server URL",
                docs: {
                    text: "The LDAP URL which consists of a scheme, address, and port. Format is <scheme>://<address>:<port> or <scheme>://<address> where scheme is either ldap or ldaps.",
                    url: "/docs/ldap-integration#configuration",
                },
            },
            validation: urlValidator,
        },
        {
            name: "use_starttls",
            component: "switch",
            fieldProps: {
                label: "Use STARTTLS",
                docs: {
                    text: "Flag to control if ldap:// connections should upgrade to TLS using STARTTLS.",
                },
            },
        },
        {
            name: "cacert",
            component: "textarea",
            fieldProps: {
                label: "CA certificate",
                docs: {
                    text: "The CA certificate of the server.",
                },
            },
        },
        {
            name: "verify_tls",
            component: "switch",
            fieldProps: {
                label: "Verify TLS",
                docs: {
                    text: "Verify TLS certificate.",
                },
            },
        },
        {
            name: "client_cert",
            component: "textarea",
            fieldProps: {
                label: "Client certificate",
                docs: {
                    text: 'The client certificate used by the "EXTERNAL" authentication mechanism.',
                },
            },
        },
        {
            name: "client_key",
            component: "writeOnlyInput",
            fieldProps: {
                label: "Client certificate key",
                clearable: false,
                docs: {
                    text: 'The key of the client certificate used by the "EXTERNAL" authentication mechanism. Enter the value itself, or a secret reference such as vault.secret(<vault config>:<path>.<key>) or system.secret(<secret name>).',
                    url: "/docs/ldap-integration#configuration",
                },
                isMultiLine: true,
            },
            fieldAttr: {
                placeholder: "Set new client certificate key",
            },
        },
        {
            name: "username",
            component: "text",
            fieldProps: {
                label: "Username",
                docs: {
                    text: "The username to authenticate. Enter the value itself, or a secret reference such as vault.secret(<vault config>:<path>.<key>) or system.secret(<secret name>).",
                    url: "/docs/ldap-integration#configuration",
                },
            },
            fieldAttr: {
                autoComplete: "new-password",
            },
        },
        {
            name: "password",
            component: "writeOnlyInput",
            fieldProps: {
                label: "Password",
                clearable: false,
                docs: {
                    text: "The password to authenticate. Enter the value itself, or a secret reference such as vault.secret(<vault config>:<path>.<key>) or system.secret(<secret name>).",
                    url: "/docs/ldap-integration#configuration",
                },
            },
            fieldAttr: {
                autoComplete: "new-password",
                placeholder: "Set new password",
            },
        },
        {
            name: "authentication_mechanism",
            component: "text",
            fieldProps: {
                label: "Authentication mechanism",
                docs: {
                    text: 'The authentication mechanism to use. Popular choices are "SIMPLE" (=unencrypted plaintext), "DIGEST-MD5", "NTLM", "CRAM-MD5" and others.',
                    url: "/docs/ldap-integration#configuration",
                },
            },
        },
        {
            name: "base_dn",
            component: "text",
            fieldProps: {
                label: "Base DN",
                docs: {
                    text: "The base container for all LDAP queries.",
                    url: "/docs/ldap-integration#configuration",
                },
            },
        },
        {
            name: "users_filter",
            component: "text",
            fieldProps: {
                label: "Users filter",
                docs: {
                    text: "The filter used to find a user.",
                    url: "/docs/ldap-integration#configuration",
                },
            },
        },
        {
            name: "username_attribute",
            component: "text",
            fieldProps: {
                label: "Username attribute",
                docs: {
                    text: "The LDAP attribute which maps to the Cloudomation username.",
                    url: "/docs/ldap-integration#configuration",
                },
            },
        },
        {
            name: "mail_attribute",
            component: "text",
            fieldProps: {
                label: "Mail attribute",
                docs: {
                    text: "The LDAP attribute containing the email address of the user.",
                    url: "/docs/ldap-integration#configuration",
                },
            },
        },
    ],
    formDTO: {
        initialValueDTO: (resourceData: any) =>
            normalizeNullableStringFields(resourceData, ["cacert", "client_cert"]),
    },
};
export default config;
